<template lang="pug">
FetchErrorTransition(:isFetching="isFetching" :error="error")
  UpsertResourceValidator(
    v-if="!isFetching && !error"
    :forEditorModel="forEditorModel"
    :businessUnitKey="businessUnitKey"
    :segmentConfiguration="segmentConfiguration"
    :enableMultilanguage="enableMultilanguage"
    :enableResourceGroups="enableResourceGroups"
    :disabled="isSaving"
    :isSaving="isSaving"
    :showAuditTrail="showAuditTrail"
    @save="save"
    @cancel="backToOverview()"
    )
</template>

<script>
import UpsertResourceValidator from './UpsertResourceValidator.vue'
import tryCall from '@v/mixins/tryCall.js'
import { defaultSegment, mapSegmentsFromApi } from './mapSegments.js'
import {
  DotnetValidationError,
  ExceptionOrigin,
  parseAxiosException,
} from '@/helpers/exceptionParser.js'
import { mapGetters, mapActions, mapState } from 'vuex'
import { startsWith, propEq, find } from 'ramda'
import ResourceType from './ResourceType.js'

export default {
  name: 'UpsertResourceWrapper',
  components: { UpsertResourceValidator },
  mixins: [tryCall],
  props: { resourceKey: String },
  data() {
    return {
      isFetching: true,
      isSaving: false,
      error: null,
      forEditorModel: null,
      segmentConfiguration: null, // todo: refactor?
      saveError: null,
      resourceSettings: null,
      feedResources: null,
      resource: null,
      resourceTypes: null,
    }
  },
  computed: {
    ...mapState('inventory', ['resources']),
    ...mapGetters('visitorClassifications', ['classificationCode', 'classification']),
    ...mapGetters('briqState', [
      'businessUnitKey',
      'userCanViewAuditTrail',
      'envFeatureFlags',
      'enabledFeatures',
    ]),
    showAuditTrail() {
      return this.resourceKey && this.userCanViewAuditTrail
    },
    enableMultilanguage() {
      return this.envFeatureFlags['enable-multilanguage']
    },
    enableResourceGroups() {
      return this.enabledFeatures.includes('ResourceGroup')
    },
    hasMultilanguage() {
      return (
        this.enableMultilanguage &&
        !!this.forEditorModel?.Resource?.TranslationInfo?.AvailableCultures?.length
      )
    },
  },
  methods: {
    ...mapActions('visitorClassifications', ['maybeFetchFirstClassification']),
    ...mapActions('inventory', ['updateResource']),
    async save(resource) {
      const resourceToSave = this.buildSaveModel(resource)
      this.isSaving = true
      const saveResourceResult = await this.tryCall(this.makeSaveThunk(resourceToSave))
      this.isSaving = false
      if (saveResourceResult.success) {
        this.$notifySuccess('Resource saved')
        const responseData = saveResourceResult.data.data
        if (responseData) {
          const resource = find(propEq('Key', responseData.ResourceKey))(responseData.ResourceList)
          const updatedData = {
            resource,
            resourceList: responseData.ResourceList,
          }
          this.updateResource(updatedData)
        }
        this.backToOverview()
      } else this.handleError(saveResourceResult.error)
    },
    buildSaveModel(resource) {
      const settings = this.forEditorModel.Settings?.map(s => ({
        BusinessUnitKey: s.BusinessUnitKey,
        ActivityKey: s.ActivityKey,
        ResourceKey: s.ResourceKey,
        SettingKey: s.SettingKey,
        Values: s.Values,
      }))
      const resourceToSave = {
        ...resource,
        TranslationInfo: this.hasMultilanguage ? resource.TranslationInfo : null,
      }
      // todo: refactor to validation; this is hacky ensurance
      if (resourceToSave.ResourceType === ResourceType.ResourceGroup)
        resourceToSave.ShowOnScheduler = false
      if (resourceToSave.ResourceType !== ResourceType.SubResource)
        resourceToSave.Settings = settings
      if (this.segmentConfiguration?.segments) {
        const activeSegments = this.segmentConfiguration.segments.filter(s => s.active)
        const segmentsConfiguration = {
          ClassificationCode: this.classificationCode,
          Pooled: this.segmentConfiguration.pooled,
          ActiveSegmentCodes: activeSegments.map(s => s.code),
          Segments: activeSegments.map(s => ({
            ClassificationCode: this.classificationCode,
            SegmentCode: s.code,
            ModifiedCapacity: s.capacity,
            Exclusive: s.exclusive,
            Priority: s.priority,
          })),
        }
        if (resourceToSave.ResourceType !== ResourceType.SubResource)
          resourceToSave.SegmentsConfigurations = [segmentsConfiguration]
      }
      return resourceToSave
    },
    handleError(e) {
      const parsedException = parseAxiosException(e)
      this.saveError = parsedException
      this.$logError(`Error expected to originate from ${parsedException.origin}`)
      this.$logError(parsedException.data)
      const msg = this.getErrorMessage(parsedException)
      this.$notifyError(msg)
    },
    getErrorMessage(parsedException) {
      if (parsedException.status === 400) return this.getBadRequestMessage(parsedException)
      return 'Something went wrong'
    },
    getBadRequestMessage(parsedException) {
      if (parsedException.origin === ExceptionOrigin.DOTNET) {
        /** @type { DotnetValidationError } */
        const dotnetError = parsedException.data
        const slugError = dotnetError.AdditionalInformation?.Slug?.[0]
        if (slugError && startsWith('Duplicate slug', slugError)) {
          return 'The provided slug is already in use by another resource'
        }
      }
      return 'Something went wrong'
    },
    makeSaveThunk(resource) {
      return async () => {
        return await this.$apiClient.post('resources/save', resource)
      }
    },
    backToOverview() {
      this.$router.push({ name: 'Resources' })
    },
    buildSegmentConfiguration(segmentConfigurations) {
      if (segmentConfigurations?.length) {
        const config = segmentConfigurations[0]
        return {
          pooled: config.Pooled,
          segments: mapSegmentsFromApi({ data: config, resourceIsNew: !this.resourceKey }),
        }
      } else {
        let priority = 1
        return {
          pooled: true,
          segments: this.classification.segments.map(s =>
            defaultSegment({ name: s.name, code: s.key, priority: priority++ }),
          ),
        }
      }
    },
    fetchEditModel() {
      const URL = this.resourceKey
        ? `resources/foreditor/${this.resourceKey}`
        : 'resources/foreditor'
      return this.$apiClient
        .get(URL)
        .then(res => {
          // todo: wrap all this shit in a model
          const data = res.data
          this.forEditorModel = data
          this.segmentConfiguration = this.buildSegmentConfiguration(data.SegmentConfigurations)
          if (this.resourceKey)
            this.$setDocumentTitle(`Resources | ${this.forEditorModel.Resource.Description}`)
          return data
        })
        .catch(e => {
          this.$notifyError('An error occured while fetching')
          throw e
        })
    },
  },
  async beforeMount() {
    this.isFetching = true
    try {
      await this.maybeFetchFirstClassification()
      await this.fetchEditModel()
    } catch (e) {
      this.error = e
    }
    this.isFetching = false
  },
  errorCaptured(e) {
    this.$logError('UpsertResourceWrapper.errorCaptured')
    this.error = e
  },
}
</script>
